import { useEffect, useRef, useState } from 'react';
import { Player } from '@remotion/player';
import { InkWipeComposition } from '../../remotion/InkWipeComposition';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const FPS = 60;
const DURATION = 42; // 0.7s — ink wipe duration

interface ArticleCardEntranceProps {
  children: React.ReactNode;
  delay?: number; // ms stagger delay before triggering animation
  className?: string;
}

export function ArticleCardEntrance({ children, delay = 0, className = '' }: ArticleCardEntranceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [done, setDone] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      setDone(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
    // visible intentionally excluded: once set true we never need to re-observe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion]);

  useEffect(() => {
    if (!visible || playing || done) return;
    const timer = setTimeout(() => setPlaying(true), delay);
    return () => clearTimeout(timer);
  }, [visible, playing, done, delay]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {children}
      {playing && !done && (
        <div
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}
          aria-hidden="true"
        >
          <Player
            component={InkWipeComposition}
            compositionWidth={400}
            compositionHeight={500}
            fps={FPS}
            durationInFrames={DURATION}
            autoPlay
            controls={false}
            style={{ width: '100%', height: '100%' }}
            onEnded={() => setDone(true)}
          />
        </div>
      )}
    </div>
  );
}
